import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const platforms = [
  {
    name: 'ISNetworld®',
    logo: 'https://www.energycontractorservices.com/wp-content/uploads/2013/04/Screen-shot-2013-05-30-at-10.38.08-AM.png',
    headline: 'The Industry Standard',
    desc: 'The leading contractor management platform. We handle setup, maintenance, RAVS Plus, and score optimization for ISNetworld accounts at every stage.',
    url: 'http://isnetworld.com',
  },
  {
    name: 'Veriforce',
    logo: 'https://www.energycontractorservices.com/wp-content/uploads/2024/06/veri-2.png',
    headline: 'Full Account Management',
    desc: 'Complete Veriforce compliance management — documentation, updates, compliance monitoring, and ongoing maintenance handled by specialists.',
    url: 'https://veriforce.com/',
  },
  {
    name: 'Avetta',
    logo: 'https://www.energycontractorservices.com/wp-content/uploads/2012/06/avetta-logo-white-768x265-300x104.png',
    headline: 'Expert Qualification',
    desc: 'Expert Avetta account setup, qualification, and ongoing compliance management. We navigate the complexity so you stay approved.',
    url: 'https://www.avetta.com',
  },
];

const capabilities = [
  { icon: 'ri-file-shield-2-line', label: 'RAVS Plus', desc: 'Advanced ISNetworld documentation managed by specialists.' },
  { icon: 'ri-heart-pulse-line', label: 'Injury Rate Reduction', desc: 'TRIR, DART, and EMR improvement programs for energy contractors.' },
  { icon: 'ri-group-line', label: 'Safety Training', desc: 'Onsite, online, classroom, and tailgate training coordination.' },
  { icon: 'ri-bar-chart-grouped-line', label: 'Score Improvement', desc: 'Targeted strategies to raise your compliance grades quickly.' },
];

export default function AboutServicesOverview() {
  const { ref: platformsRef, isVisible: platformsVisible } = useScrollAnimation<HTMLDivElement>();
  const { ref: capRef, isVisible: capVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="bg-[#F5F0E8]">
      {/* Mission statement */}
      <div className="px-6 lg:px-14 py-20 border-b border-[#1A1409]/08">
        <div className="max-w-5xl mx-auto text-center">
          <p className="overline mb-5">Our Purpose</p>
          <p className="font-serif text-6xl text-[#C9A030]/18 leading-none select-none mb-1">&ldquo;</p>
          <p className="font-serif text-2xl lg:text-4xl font-light text-[#1A1409] leading-relaxed italic max-w-3xl mx-auto">
            We help energy contractors navigate the requirements of ISNetworld®, Veriforce, and Avetta — so they can focus on delivering safe, high-quality work.
          </p>
          <div className="w-12 h-0.5 bg-[#C9A030] mx-auto mt-8 mb-5" />
          <p className="text-[#9A9A9A] text-[11px] tracking-[0.3em] uppercase font-medium">
            Energy Contractor Services &mdash; ECS
          </p>
        </div>
      </div>

      {/* Platform Mastery */}
      <div className="px-6 lg:px-14 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="overline mb-4">Platform Mastery</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#1A1409] leading-tight gold-line">
              Three Platforms. One Expert Team.
            </h2>
          </div>

          <div
            ref={platformsRef}
            className={`grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1A1409]/08 fade-up ${platformsVisible ? 'visible' : ''}`}
          >
            {platforms.map((p, idx) => (
              <div
                key={p.name}
                className="bg-[#F5F0E8] hover:bg-white transition-colors duration-300 p-9 flex flex-col"
                style={{ transitionDelay: `${idx * 0.1}s` }}
              >
                <div className="w-full h-14 flex items-center mb-6">
                  <img
                    src={p.logo}
                    alt={p.name}
                    className="max-h-12 w-auto object-contain opacity-70"
                  />
                </div>
                <div className="w-8 h-0.5 bg-[#C9A030] mb-5" />
                <p className="text-[#C9A030] text-[10px] tracking-[0.25em] uppercase font-semibold mb-2">
                  {p.headline}
                </p>
                <p className="text-[#5A5A5A] text-sm font-light leading-relaxed flex-1 mb-5">
                  {p.desc}
                </p>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-[#1A1409]/50 text-[10px] tracking-[0.2em] uppercase font-medium hover:text-[#C9A030] transition-colors cursor-pointer"
                >
                  About {p.name} &rarr;
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional capabilities */}
      <div className="bg-[#0A0A0A] px-6 lg:px-14 py-16">
        <div className="max-w-6xl mx-auto">
          <div
            ref={capRef}
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F2EDE3]/05 fade-up ${capVisible ? 'visible' : ''}`}
          >
            {capabilities.map((cap, idx) => (
              <div
                key={idx}
                className="bg-[#0A0A0A] p-7 hover:bg-[#111111] transition-colors"
                style={{ transitionDelay: `${idx * 0.07}s` }}
              >
                <div className="w-9 h-9 flex items-center justify-center mb-4">
                  <i className={`${cap.icon} text-[#C9A030] text-xl`} />
                </div>
                <p className="text-[#F2EDE3] text-sm font-medium mb-2">{cap.label}</p>
                <p className="text-[#9A9A9A] text-xs font-light leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
          {/* Mid-page CTA */}
          <div className="mt-10 flex flex-wrap items-center justify-between gap-5 pt-8 border-t border-[#C9A030]/12">
            <p className="text-[#9A9A9A] text-sm font-light">
              Get a compliance expert in your corner — starting today.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="bg-[#C9A030] text-[#0A0A0A] px-7 py-3 text-[11px] font-bold tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-colors hover:bg-[#D4B045]"
              >
                Contact ECS
              </Link>
              <a
                href="tel:4054081117"
                className="border border-[#F2EDE3]/20 text-[#F2EDE3]/60 px-7 py-3 text-[11px] font-medium tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-colors hover:border-[#C9A030]/50 hover:text-[#C9A030]"
              >
                Call 405-408-1117
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
